import { useEffect, useId, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  SPORTS_SEARCH_MIN_LENGTH,
  sportsMenuFullQuery,
  sportsSearchQuery,
  type SearchCategoryDto,
  type SearchEventDto,
  type SearchTournamentDto,
  type SportMenuItem,
  type TournamentMenuItem,
} from '@/api/queries';
import { formatSearchEventDate } from '@/lib/date';
import { cn } from '@/lib/cn';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { sportIcon, toSlug } from '@/shell/sports-menu-utils';
import { Icon } from '@/ui/common';
import { SearchCategoryItem } from './SearchCategoryItem';
import { SearchEventRow } from './SearchEventRow';
import { SearchSectionHeader } from './SearchSectionHeader';
import { SearchTournamentItem } from './SearchTournamentItem';

const SEARCH_DEBOUNCE_MS = 750;
const SUGGESTION_LIMIT = 6;

export interface SportsSearchNavigateTarget {
  kind: 'category' | 'tournament' | 'event';
  sport: string;
  category: string;
  tournament?: string;
  eventId?: string;
}

export interface SportsSearchPanelProps {
  query: string;
  onQueryChange: (value: string) => void;
  onNavigate: (target: SportsSearchNavigateTarget) => void;
  className?: string;
  /** Autofocus the input when the panel mounts / opens. */
  autoFocus?: boolean;
}

function collectTopLeagueSuggestions(
  sports: SportMenuItem[] | undefined,
): TournamentMenuItem[] {
  if (!sports?.length) return [];
  const items: TournamentMenuItem[] = [];
  for (const sport of sports) {
    for (const category of sport.Categories ?? []) {
      if (!category.Name.toLowerCase().includes('top league')) continue;
      for (const tournament of [...category.Tournaments].sort((a, b) => a.Position - b.Position)) {
        items.push(tournament);
        if (items.length >= SUGGESTION_LIMIT) return items;
      }
    }
  }
  return items;
}

export function SportsSearchPanel({
  query,
  onQueryChange,
  onNavigate,
  className,
  autoFocus = true,
}: SportsSearchPanelProps) {
  const { t, i18n } = useTranslation();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const trimmedDebounced = debouncedQuery.trim();
  const searching = trimmedDebounced.length >= SPORTS_SEARCH_MIN_LENGTH;

  const { data: sportsMenu } = useQuery(sportsMenuFullQuery());
  const suggestions = collectTopLeagueSuggestions(sportsMenu);

  const {
    data: results,
    isFetching,
    isError,
  } = useQuery(sportsSearchQuery(trimmedDebounced));

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const categories = results?.Categories ?? [];
  const tournaments = results?.Tournaments ?? [];
  const events = results?.Events ?? [];
  const hasAnyResults =
    categories.length > 0 || tournaments.length > 0 || events.length > 0;
  const showResultsBody = searching;
  const showNoResults = searching && !isFetching && !isError && !hasAnyResults;

  const selectCategory = (item: SearchCategoryDto) => {
    onNavigate({
      kind: 'category',
      sport: toSlug(item.Sport),
      category: toSlug(item.Name),
    });
  };

  const selectTournament = (item: Pick<SearchTournamentDto, 'Sport' | 'Category' | 'Name'>) => {
    onNavigate({
      kind: 'tournament',
      sport: toSlug(item.Sport),
      category: toSlug(item.Category),
      tournament: toSlug(item.Name),
    });
  };

  const selectEvent = (item: SearchEventDto) => {
    onNavigate({
      kind: 'event',
      sport: toSlug(item.Sport),
      category: toSlug(item.Category),
      tournament: toSlug(item.Tournament),
      eventId: String(item.Id),
    });
  };

  const searchLabel = t('Search events', { defaultValue: 'Search events' });

  return (
    <div
      role="dialog"
      aria-label={searchLabel}
      className={cn(
        'flex max-h-[min(460px,calc(100dvh-7rem))] flex-col overflow-hidden rounded-2xl bg-bg-elevated shadow-lg',
        className,
      )}
    >
      <div className="shrink-0 px-3 pt-3 pb-2">
        <label htmlFor={inputId} className="sr-only">
          {searchLabel}
        </label>
        <div
          className={cn(
            'flex h-11 w-full items-center gap-2.5 rounded-md border bg-bg-surface px-4',
            'border-border-default focus-within:border-2 focus-within:border-border-focus',
          )}
        >
          <Icon name="search" size={16} className="shrink-0 text-icon-muted" />
          <input
            ref={inputRef}
            id={inputId}
            type="search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder={searchLabel}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            className={cn(
              'min-w-0 flex-1 bg-transparent text-body-l text-text-primary outline-none',
              'placeholder:text-text-secondary',
            )}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-x-clip overflow-y-auto pb-2">
        {!showResultsBody && (
          <>
            <SearchSectionHeader
              label={t('Suggestions', { defaultValue: 'Suggestions' })}
            />
            {suggestions.length === 0 ? (
              <p className="px-3 py-2 text-[13px] text-text-secondary">
                {t('Start typing to search', {
                  defaultValue: 'Start typing to search sports, leagues, and events.',
                })}
              </p>
            ) : (
              suggestions.map((item) => (
                <SearchTournamentItem
                  key={`${item.Sport}-${item.Category}-${item.Id}`}
                  label={item.Name}
                  subtitle={item.Category}
                  tournamentName={item.Name}
                  categoryName={item.Category}
                  onSelect={() => selectTournament(item)}
                />
              ))
            )}
          </>
        )}

        {showResultsBody && isFetching && !hasAnyResults && (
          <ul className="flex flex-col gap-1 px-3 py-2" aria-busy="true">
            {Array.from({ length: 5 }).map((_, index) => (
              <li key={index} className="h-10 animate-pulse rounded-md bg-bg-surface" />
            ))}
          </ul>
        )}

        {showResultsBody && isError && (
          <p className="px-3 py-4 text-center text-[13px] text-text-secondary">
            {t('Search failed', { defaultValue: 'Could not load search results.' })}
          </p>
        )}

        {showNoResults && (
          <p className="px-3 py-4 text-center text-[13px] text-text-secondary">
            {t('NoSearchResultsfound', {
              defaultValue: 'No results found.',
            })}
          </p>
        )}

        {showResultsBody && hasAnyResults && (
          <>
            {categories.length > 0 && (
              <>
                <SearchSectionHeader
                  label={t('Categories', { defaultValue: 'Categories' })}
                  count={categories.length}
                />
                {categories.map((item) => (
                  <SearchCategoryItem
                    key={`${item.SportId}-${item.Id}`}
                    label={item.Name}
                    sportIconName={sportIcon(toSlug(item.Sport))}
                    onSelect={() => selectCategory(item)}
                  />
                ))}
              </>
            )}

            {tournaments.length > 0 && (
              <>
                <SearchSectionHeader
                  label={t('Tournaments', { defaultValue: 'Tournaments' })}
                  count={tournaments.length}
                />
                {tournaments.map((item) => (
                  <SearchTournamentItem
                    key={`${item.SportId}-${item.CategoryId}-${item.Id}`}
                    label={item.Name}
                    subtitle={item.Category}
                    tournamentName={item.Name}
                    categoryName={item.Category}
                    onSelect={() => selectTournament(item)}
                  />
                ))}
              </>
            )}

            {events.length > 0 && (
              <>
                <SearchSectionHeader
                  label={t('Events', { defaultValue: 'Events' })}
                  count={events.length}
                />
                {events.map((item) => (
                  <SearchEventRow
                    key={item.Id}
                    date={formatSearchEventDate(item.Date, i18n.language)}
                    teams={`${item.Home} – ${item.Away}`}
                    breadcrumb={`${item.Sport} › ${item.Category} › ${item.Tournament}`}
                    onSelect={() => selectEvent(item)}
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
